import { Link } from "react-router-dom";
import useReveal from "../hooks/useReveal";
import "./About.css";
import { Helmet } from 'react-helmet-async';

const agrhomabout = "/images/homeabou.webp";
const heroImg = "/images/abouthero.webp";
const HIGHLIGHTS = [
  {
    title: "Traditional Fluxo / Masala Expertise",
    desc: "Finest quality traditional Indian fluxo and masala agarbattis crafted with devotion and precision.",
  },
  {
    title: "Natural Blended Formulations",
    desc: "Made using flower extracts, essential oils, halmaddi resin, herbs, leaves, tree barks and other aromatic ingredients.",
  },
  {
    title: "Perfumed Incense Range",
    desc: "Specially crafted perfumed incense sticks that recreate the feel and essence of original floral aromas.",
  },
  {
    title: "Innovative Packaging",
    desc: "Premium shelf-ready packaging that enhances product appeal and brand value in every market.",
  },
];

const PILLARS = [
  {
    icon: "🌿",
    title: "Pure Ingredients",
    desc: "We are strict in selecting and handling raw materials so customers receive the finest and purest products.",
  },
  {
    icon: "🏆",
    title: "Premium Quality",
    desc: "Our quality-first manufacturing methods make every fragrance refined, lasting and market-trusted.",
  },
  {
    icon: "🤝",
    title: "Strong Teamwork",
    desc: "From manufacturing to marketing, our growth is powered by teamwork, participation and shared commitment.",
  },
  {
    icon: "🧪",
    title: "Continuous R&D",
    desc: "Our R&D team keeps developing newer fragrances while maintaining non-toxic fragrance standards.",
  },
];

const FRAGRANCE_POINTS = [
  "Bhagwathi Flora",
  "Shree Satyanarayan",
  "Anmol Nagchampa",
  "Shree Satyavinayak",
  "Many more signature fragrances",
];

export default function About() {
  useReveal();

  return (
    <>
    <main className="about-page-main" style={{ paddingTop: "70px" }}>
       <Helmet>
        <title>About Prakhyat Agarbatti | Premium Incense Brand — Mumbai</title>
        <meta
          name="description"
          content="Learn about Prakhyat Agarbatti — a premium incense brand by Pushpak Aroma Products. Handcrafted Masala Agarbatti, Dhoop Cones & Dhoop Sticks made from 100% natural ingredients including flower extracts, essential oils & halmaddi resin."
        />
        <meta
          name="keywords"
          content="about prakhyat agarbatti, pushpak aroma products, masala agarbatti brand, natural incense manufacturer, premium agarbatti brand india, agarbatti manufacturer mumbai, handcrafted incense india, traditional agarbatti, fluxo agarbatti, natural incense brand"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://prakhyatagarbatti.com/about" />
      </Helmet>
      <div
        className="page-hero"
        style={{
          backgroundImage: `url(${heroImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="overlay"></div>

        <div className="hero-content container">
          <div className="hero-badge reveal">The Eternal Fragrance</div>
          <h1 className="reveal">About Prakhyat Agarbattis</h1>
        
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="about-intro">
            <div className="reveal-l about-intro-media">
              <img
                src={agrhomabout}
                alt="Prakhyat Agarbattis premium incense products"
                loading="lazy"
                className="about-img"
              />
            
            </div>

            <div className="reveal-r">
              <div className="eyebrow">Introduction</div>
              <h2 className="h-display">
                A Premium Brand Rooted in Fragrance, Faith and Tradition
              </h2>
              <div className="divider-ornament">
                <span />
                <span>🌼</span>
                <span />
              </div>

              <p className="h-sub">
                <strong>PRAKHYAT Agarbattis</strong> is a premium and highly
                appreciated brand among consumers, operating under the flagship
                of <strong>Pushpak Aroma Products</strong>, makers of finest
                quality traditional Indian Fluxo / Masala Agarbattis and
                perfumed incense sticks since many years.
              </p>

              <p className="h-sub" style={{ marginTop: "1rem" }}>
                In the modern era, agarbattis and incense sticks are used not
                only for prayers across religions, but also for aromatherapy,
                yoga, meditation, Vaastu Shanti, and for purifying and cleansing
                the atmosphere. Pleasant fragrance is known to relieve stress,
                reduce tension, and bring peace to the mind, body and soul.
              </p>

              <div className="about-cta-row">
                <Link to="/products" className="btn btn-primary">
                  Explore Products
                </Link>
                <Link to="/contact" className="btn btn-green">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-story">
        <div className="about-story__bg" />
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom: "2.5rem" }}>
            <div
              className="eyebrow"
              style={{ justifyContent: "center", color: "var(--yellow)" }}
            >
              Our Essence
            </div>
            <h2 className="h-display on-dark">
              Welcome to Prakhyat Agarbattis — The Eternal Fragrance
            </h2>
          </div>

          <div className="about-story__text reveal">
            <p>
              <em>
                “Mystic nature of the incense odour fills me with joy, thoughts
                become my joy’s wheel, while moments painted it with glorious
                feel.”
              </em>
            </p>
            <p>
              At Prakhyat, fragrance is more than aroma — it is emotion,
              devotion and experience. Our incense sticks are created to elevate
              prayer, bring calmness to everyday routines and enrich spaces with
              an atmosphere of purity and serenity.
            </p>
            <p>
              Every fragrance is developed with the intention of preserving the
              essence of nature while delivering a refined and memorable sensory
              experience that remains close to the heart.
            </p>
          </div>
        </div>
      </section>

      <section className="section about-overview-section">
        <div className="container">
          <div className="about-section-head text-center reveal">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              Overview
            </div>
            <h2 className="h-display">Ancient Tradition, Modern Relevance</h2>
          </div>

          <div className="about-overview-card reveal">
            <p>
              Agarbattis and incense sticks are believed to be holy and have
              been used since ancient times in India. The oldest source of
              incense is found in the Vedas, where Rishi-Munis used it while
              performing Yagyas and Havanas.
            </p>
            <p>
              Incense burning has long been valued for creating a pleasing aroma
              and for worship, where the fragrant fumes of burning incense are
              believed to help prayers reach God.
            </p>
            <p>
              Today, incense continues to hold deep relevance in prayers,
              meditation, aromatherapy, yoga, Vaastu Shanti and atmosphere
              cleansing. It helps create positivity and a soothing environment
              that brings balance to mind, body and soul.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="about-section-head text-center reveal">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              What Makes Us Special
            </div>
            <h2 className="h-display">Crafted with Natural Blends & Fine Aroma</h2>
          </div>

          <div className="about-highlight-grid">
            {HIGHLIGHTS.map((item, index) => (
              <div
                key={index}
                className="about-highlight-card reveal"
                style={{ transitionDelay: `${index * 0.08}s` }}
              >
                <div className="about-highlight-card__count">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="about-fragrance-box reveal">
            <div className="about-fragrance-box__left">
              <div className="eyebrow">Signature Blends</div>
              <h3>Beloved Fragrances from the Prakhyat Collection</h3>
              <p>
                Our unique blended fluxo agarbattis are formulated with extracts
                of flowers, essential oils, halmaddi resin, tree barks, leaves
                and herbs that create a distinctive aroma and natural richness.
              </p>
            </div>

            <div className="about-fragrance-tags">
              {FRAGRANCE_POINTS.map((item, index) => (
                <span
                  key={index}
                  className="about-fragrance-tag reveal"
                  style={{ transitionDelay: `${index * 0.06}s` }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section about-quality-section">
        <div className="container">
          <div className="about-split-grid">
            <div className="about-info-panel reveal">
              <div className="eyebrow">Our Quality</div>
              <h2 className="h-display">Quality Comes First in Everything We Make</h2>
              <p>
                Quality is the first thing that makes our products stand out in
                the market. With years of manufacturing experience and precise
                preparation methods, we ensure consistency, purity and
                excellence in every product.
              </p>
              <p>
                We are highly selective in choosing raw materials and careful in
                handling them, so the final compounds delivered to customers are
                refined, pure and reliable.
              </p>
              <p>
                We also focus on innovative packaging that enhances shelf appeal
                and gives every product the importance it deserves.
              </p>
            </div>

            <div className="about-info-panel reveal">
              <div className="eyebrow">Team Work</div>
              <h2 className="h-display">Driven by Collaboration, Innovation and Care</h2>
              <p>
                A strong organization always depends on its best teamwork.
                Prakhyat believes in teamwork and participation at every level —
                from manufacturing to marketing.
              </p>
              <p>
                Our R&D team continuously works to add new fragrances to our
                portfolio by innovating fresh aromatic experiences while also
                complying with non-toxic fragrance practices.
              </p>
              <p>
                This balance of teamwork, innovation and responsibility helps us
                keep growing while staying true to our brand values.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--light-bg)" }}>
        <div className="container">
          <div className="text-center reveal">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              Our Foundation
            </div>
            <h2 className="h-display">The Pillars Behind Prakhyat</h2>
          </div>

          <div className="about-values">
            {PILLARS.map((item, index) => (
              <div
                key={index}
                className="av-card reveal"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className="av-icon">{item.icon}</div>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-motto-section">
        <div className="container">
          <div className="about-motto-card reveal">
            <div className="eyebrow" style={{ justifyContent: "center" }}>
              Our Motto
            </div>
            <h2>We give a reason to breathe genuine, pure and infinite fragrances.</h2>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}